// 📄 services/perfilValoresService.ts
// Perfiles de valores por cliente (docs/plans/plan-perfiles-valores.md):
// datos de un cliente que se repiten al adaptar iFlows. Todo pasa por
// onehubApi (/v1/value-profiles): los valores viajan solo a quien puede
// usar el perfil (creador, owner o admin) y se guardan cifrados con KMS.
import { getAuth } from 'firebase/auth';
import { IflowApiError } from './iflowService.ts';

export type TipoValorPerfil = 'url' | 'alias' | 'location' | 'filtro' | 'api' | 'otro';

export const TIPOS_VALOR_PERFIL: { value: TipoValorPerfil; label: string }[] = [
  { value: 'url', label: 'URL' },
  { value: 'alias', label: 'Alias de credencial' },
  { value: 'location', label: 'Location ID (Cloud Connector)' },
  { value: 'filtro', label: 'Filtro' },
  { value: 'api', label: 'API / ruta' },
  { value: 'otro', label: 'Otro' }
];

export interface ValorPerfil {
  id?: string;
  nombre: string;
  valor: string;
  tipo: TipoValorPerfil;
  // Opcional: el valor que este reemplaza al "aplicar el perfil" (P3).
  reemplaza: string;
}

export interface ResumenPerfil {
  id: string;
  nombre: string;
  clienteId: string;
  ambiente: string;
  cantidad: number;
  autorUid: string;
  autorNombre: string;
  revision: number;
  creadoEn: string | null;
  actualizadoEn: string | null;
}

export interface PerfilValores extends ResumenPerfil {
  valores: ValorPerfil[];
}

// Espejo de LIMITES_PERFILES (functions/services/perfilValoresService.js).
export const LIMITES_PERFILES = { porOrg: 50, valores: 200, valor: 4000 };

const pedir = async <T,>(metodo: string, ruta: string, body?: object): Promise<T> => {
  const user = getAuth().currentUser;
  if (!user) throw new Error('No se detectó una sesión activa. Por favor, reingresa al sistema.');
  const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/value-profiles${ruta}`, {
    method: metodo,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  if (!res.ok) {
    const { code = 'ERROR', error, ...extra } = await res.json().catch(() => ({}));
    throw new IflowApiError(res.status, code, error || `Error ${res.status} al comunicarse con el servidor.`, extra);
  }
  return res.json();
};

const cuerpo = (p: { nombre?: string; clienteId?: string; ambiente?: string; valores?: ValorPerfil[] }) => ({
  name: p.nombre,
  clientId: p.clienteId,
  environment: p.ambiente,
  values: p.valores?.map((v) => ({ id: v.id, name: v.nombre, value: v.valor, type: v.tipo, replaces: v.reemplaza }))
});

export const listarPerfiles = (orgId: string) => pedir<{ perfiles: ResumenPerfil[] }>('GET', `/${orgId}`).then((r) => r.perfiles);
export const obtenerPerfil = (orgId: string, id: string) => pedir<PerfilValores>('GET', `/${orgId}/${id}`);
export const crearPerfil = (orgId: string, datos: { nombre: string; clienteId?: string; ambiente?: string; valores?: ValorPerfil[] }) =>
  pedir<{ id: string; revision: number }>('POST', `/${orgId}`, cuerpo(datos));
export const guardarPerfil = (orgId: string, id: string, datos: { nombre?: string; clienteId?: string; ambiente?: string; valores?: ValorPerfil[] }, revisionBase: number) =>
  pedir<{ id: string; revision: number }>('PUT', `/${orgId}/${id}`, { ...cuerpo(datos), baseRevision: revisionBase });
export const borrarPerfil = (orgId: string, id: string) => pedir<{ id: string }>('DELETE', `/${orgId}/${id}`);

// ── Aplicar un perfil (P3) ──

export interface CambioPorPerfil {
  clave: string;
  antes: string;
  despues: string;
  // Nombres de los valores del perfil que intervinieron.
  reglas: string[];
}

/**
 * Aplica las reglas "Reemplaza a -> Valor" de un perfil DENTRO del texto de
 * cada valor (así una regla con el host cambia todas las URLs que lo
 * contienen). En UNA sola pasada: el resultado de una regla nunca dispara
 * otra; ante coincidencias superpuestas gana la regla más larga. Devuelve
 * solo lo que cambia.
 */
export const calcularAplicacion = (
  valores: { clave: string; valor: string }[],
  perfil: { valores: ValorPerfil[] }
): CambioPorPerfil[] => {
  const reglas = new Map<string, ValorPerfil>();
  for (const v of perfil.valores) if (v.reemplaza && v.reemplaza !== v.valor && !reglas.has(v.reemplaza)) reglas.set(v.reemplaza, v);
  if (!reglas.size) return [];
  const escapar = (t: string) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const patron = new RegExp([...reglas.keys()].sort((x, y) => y.length - x.length).map(escapar).join('|'), 'g');
  return valores.flatMap(({ clave, valor }) => {
    const usadas = new Set<string>();
    const despues = valor.replace(patron, (m) => {
      const r = reglas.get(m)!;
      usadas.add(r.nombre);
      return r.valor;
    });
    return despues !== valor ? [{ clave, antes: valor, despues, reglas: [...usadas] }] : [];
  });
};
